import { Component, NgZone, OnInit } from '@angular/core';
import { HttpFunctionResult } from 'src/app/_interfaces/HttpFunctionResult';
import { FeedbackService } from 'src/app/_provider/feedback.service';
import { GeneralService } from 'src/app/_provider/general.service';
import { ProcessService } from 'src/app/_provider/processes/processes.service';
import { ProcessBase, ProcessType } from 'src/app/_provider/processes/processes.types';
import { PopoverListItem, PopoverPage } from '../../popover/popover.page';
import { ProcessDetailPage } from './process-detail/process-detail.page';
import { WawiConnectorService } from 'src/app/_provider/wawi-connector/wawi-connector.service';

const LIMIT = 50

@Component({
    selector: 'process-list',
    templateUrl: './process-list.component.html',
    styleUrls: ['./process-list.component.scss'],
    standalone: false
})
export class ProcessListComponent implements OnInit {

  public isLoading: boolean = true;
  public isMoreLoading: boolean = false;

  public processes: ProcessBase[] = [];
  public typeList: ProcessType[] = []

  public selectedProcessType: ProcessType = {id: -1, name: "Alle"}
  public searchText: string = '';

  private offset: number = 0

  private _showMore: boolean = false;

  public get showMore(): boolean {
    return this._showMore && this.processes.length >= LIMIT;
  }

  constructor(
    private processSrv: ProcessService,
    private generalSrv: GeneralService,
    private feedback: FeedbackService,
    private ngZone: NgZone,
    public wawiConnector: WawiConnectorService
  ) { }

  async ngOnInit() {
    await Promise.all([ this.loadTypeList(), this.loadProcessList(), this.wawiConnector.initConnector()])
  }

  private async loadTypeList(){
    const result: HttpFunctionResult<ProcessType[]> = await this.processSrv.getTypeList()
    if(result.success) this.typeList = result.data
  }

  private async loadProcessList(){

    this.offset = 0
    this._showMore = true

    this.ngZone.run(() => this.isLoading = true)

    const result: HttpFunctionResult<ProcessBase[]> = await this.processSrv.getProcessList(
      this.generalSrv.activeCrmValue.managerId, 
      this.selectedProcessType.id, 
      this.searchText,
      this.offset, 
      LIMIT
    )

    this.ngZone.run(() => this.isLoading = false)
    if(result.success) this.processes = result.data
  }

  private searchTimeout: any
  public async onSearchValueChange(){
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(async () => await this.loadProcessList(), 300);
  }

  public async openTypeFilter(event: MouseEvent) {
    const list: PopoverListItem[] = [
      { id: -1, description: "Alle", selected: this.selectedProcessType.id === -1 },
      ...this.typeList.map(t => ({
        id: t.id,
        description: t.name,
        selected: this.selectedProcessType.id === t.id
      }))
    ]

    const result: HttpFunctionResult<PopoverListItem> = await this.feedback.showPopover({
      event: event,
      component: PopoverPage,
      componentProps: { list }
    })
    
    if(result.success) {
      this.selectedProcessType = this.typeList.find(t => t.id === result.data.id) || {id: -1, name: "Alle"}
      await this.loadProcessList()
    } 
  }

  public async loadMoreProcesses() {
    this.ngZone.run(() => this.isMoreLoading = true)
    this.offset += LIMIT;

    const result: HttpFunctionResult<ProcessBase[]> = await this.processSrv.getProcessList(
      this.generalSrv.activeCrmValue.managerId, 
      this.selectedProcessType.id, 
      this.searchText,
      this.offset, 
      LIMIT
    )
    this.ngZone.run(() => this.isMoreLoading = false)

    if(result.success){
      if(result.data.length < LIMIT) this._showMore = false;
      this.processes.push(...result.data);
    }
  }

  public async openProcessDetails(event: MouseEvent, process: ProcessBase) {

    const showModal = async () => {
      await this.feedback.showModal({
        component: ProcessDetailPage,
        componentProps: { process },
      })
    }

    if(this.wawiConnector.connector.executable){
      const list: PopoverListItem[] = [
        {id: 0, description: `In ${this.wawiConnector.appName} öffnen`, icon: "wawitogo"},
        {id: 1, description: "Positionen anzeigen", icon: "fa-list"}
      ]

      const result: HttpFunctionResult<PopoverListItem> = await this.feedback.showPopover({ event, component: PopoverPage, componentProps: {list} })

      if(!result.success) return;
      else if(result.data.id == 0) {
        const openResult: HttpFunctionResult = await this.wawiConnector.connector.openProcess(process.id)
        if(openResult.success) this.feedback.showInformationToast(`Vorgang wurde in ${this.wawiConnector.appName} geöffnet.`, "Information", 2000)
        else this.feedback.showError(openResult.message)
      }
      else if(result.data.id == 1) await showModal()

    }else await showModal()
  }
}
